import React, { useState } from "react";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { showPromiseToast } from "../../services/toastService.js";
import { setSetting } from "../../services/settingsService.js";
import { useAuth } from "../../contexts/AuthContextProvider.jsx";
import { IoText } from "react-icons/io5";
import { MdNumbers } from "react-icons/md";
import { IoMdSwitch } from "react-icons/io";
import { TbClockCode } from "react-icons/tb";

const SettingsManagement = ({ state, setState, setIsDirty }) => {
	const [editingKey, setEditingKey] = useState(null);
	const [editValue, setEditValue] = useState("");
	const { user } = useAuth();

	const handleEditClick = (key, value) => {
		setEditingKey(key);
		setEditValue(value);
		setIsDirty(true);
	};

	const handleInputChange = (e) => {
		setEditValue(e.target.value);
	};

	const handleSave = (key) => {
		showPromiseToast(setSetting(key, editValue), {
			pending: "Aggiornamento impostazione in corso...",
			success: "Impostazione aggiornata con successo",
		}).then(() => {
			const updatedSettings = state.map((setting) =>
				setting.k === key ? { ...setting, value: editValue } : setting
			);
			setState(updatedSettings);
			setEditingKey(null);
			setIsDirty(false);
		});
	};

	const handleCancel = () => {
		setEditingKey(null);
		setEditValue("");
		setIsDirty(false);
	};

	const renderValue = (s) => {
		const { value, type } = s;
		if (value === "1" && type === "bool")
			return <strong className="text-green-700">True</strong>;
		if (value === "0" && type === "bool")
			return <strong className="text-red-700">False</strong>;
		if (type === "string") return <span className="italic">{value}</span>;
		if (type === "int") return <span className="font-mono text-lg">{value}</span>;
		if (type === "cron") return <span className="font-mono text-lg">{value}</span>;

		return value;
	};

	const renderType = (type) => {
		if (type === "string")
			return (
				<span className="text-2xl mx-2">
					<IoText />
				</span>
			);
		if (type === "int")
			return (
				<span className="text-2xl mx-2">
					<MdNumbers />
				</span>
			);
		if (type === "bool")
			return (
				<span className="text-3xl mx-2">
					<IoMdSwitch />
				</span>
			);
		if (type === "cron")
			return (
				<span className="text-3xl mx-2">
					<TbClockCode />
				</span>
			);
		return type;
	};

	const renderInputField = (setting) => {
		if (setting.type === "int") {
			return (
				<input
					type="number"
					value={editValue}
					onChange={handleInputChange}
					className="border border-gray-300 p-2 rounded w-full"
				/>
			);
		} else if (setting.type === "bool") {
			return (
				<select
					value={editValue}
					onChange={handleInputChange}
					className="border border-gray-300 p-2 rounded w-full"
				>
					<option value="1">True</option>
					<option value="0">False</option>
				</select>
			);
		} else if (setting.type === "cron") {
			return (
				<input
					type="text"
					placeholder="* * * * *"
					value={editValue}
					onChange={handleInputChange}
					className="border border-gray-300 p-2 rounded w-full font-mono"
				/>
			);
		} else {
			return (
				<input
					type="text"
					value={editValue}
					onChange={handleInputChange}
					className="border border-gray-300 p-2 rounded w-full"
				/>
			);
		}
	};

	const canEditChecks = (lvl) => {
		return (user.level > lvl && lvl !== 0) || lvl === -1;
	};

	return (
		<div className="">
			<h2 className="text-xl md:text-2xl font-bold mb-4 text-center">
				Gestione Impostazioni
			</h2>

			{/* Tabella Impostazioni */}
			<div className="overflow-x-auto w-full">
				<table className="w-full border-collapse border border-gray-300 hidden md:table">
					<thead>
						<tr className="bg-gray-200">
							<th className="border border-gray-300 p-2 w-1/8">Tipo</th>
							<th className="border border-gray-300 p-2 w-1/4">Chiave</th>
							<th className="border border-gray-300 p-2 w-1/2">Valore</th>
							<th className="border border-gray-300 p-2 w-1/8">Azione</th>
						</tr>
					</thead>
					<tbody>
						{state.map((setting, index) => (
							<tr key={index} className="hover:bg-gray-200 text-center table-row">
								<td className=" border border-gray-300 p-2">
									<span className="flex justify-center">
										{renderType(setting.type)}
									</span>
								</td>
								<td className="border border-gray-300 p-2 font-semibold">
									{setting.label}
								</td>
								<td className="border border-gray-300 p-2">
									{editingKey === setting.k
										? renderInputField(setting)
										: renderValue(setting)}
								</td>
								<td className="border border-gray-300 p-2">
									{canEditChecks(setting.lvl_write) ? (
										""
									) : editingKey === setting.k ? (
										<div className="flex justify-center">
											<button
												id="cancel"
												aria-label="Cancel"
												onClick={handleCancel}
												className="bg-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-400"
											>
												<FaTimes />
											</button>
											<button
												id="save"
												aria-label="Save"
												onClick={() => handleSave(setting.k)}
												className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 ml-2"
											>
												<FaSave />
											</button>
										</div>
									) : (
										<button
											id="edit"
											aria-label="Edit"
											onClick={() =>
												handleEditClick(setting.k, setting.value)
											}
											className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
										>
											<FaEdit />
										</button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>

				{/* Visualizzazione Mobile */}
				<div className="md:hidden">
					{state.map((setting, index) => (
						<div key={index} className="border border-gray-300 p-4 mb-2 rounded">
							<div className="mb-2">
								<span className="flex justify-start font-bold">
									{renderType(setting.type)}
									{setting.label}
								</span>
							</div>
							<div className="mb-2">
								<span className="font-semibold">Valore:</span>{" "}
								{editingKey === setting.k
									? renderInputField(setting)
									: renderValue(setting)}
							</div>
							<div className="flex justify-end">
								{canEditChecks(setting.lvl_write) ? (
									<button
										id="edit"
										aria-label="Edit"
										disabled
										className="bg-gray-700  text-white px-3 py-1 rounded"
									>
										Non Modificabile
									</button>
								) : editingKey === setting.k ? (
									<>
										<button
											id="cancel"
											aria-label="Cancel"
											onClick={handleCancel}
											className="bg-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-400"
										>
											Annulla
										</button>
										<button
											id="save"
											aria-label="Save"
											onClick={() => handleSave(setting.k)}
											className="bg-green-500 text-white px-3 py-1 rounded hover:bg-blue-600 ml-2"
										>
											Salva
										</button>
									</>
								) : (
									<button
										id="edit"
										aria-label="Edit"
										onClick={() => handleEditClick(setting.k, setting.value)}
										className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
									>
										Modifica
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default SettingsManagement;
